import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Input, Select } from '@/components/ui/Field';
import { Banner, ErrorState, Skeleton } from '@/components/ui/Feedback';
import { PageHeader } from '@/app/PlaceholderPage';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';

interface Period {
  id: string;
  semester: number;
  programYear: number;
  phase: string | null;
  name: string;
  description: string | null;
  startDate: string | null;
  endDate: string | null;
  status: 'upcoming' | 'active' | 'locked';
}

interface PeriodInput {
  semester: number;
  name: string;
  description: string | null;
  phase: string | null;
  startDate: string | null;
  endDate: string | null;
}

const PHASES = [
  { value: 'baseline', label: 'Baseline' },
  { value: 'monitoring_1', label: 'Monitoring 1' },
  { value: 'monitoring_2', label: 'Monitoring 2' },
  { value: 'monitoring_3', label: 'Monitoring 3' },
  { value: 'monitoring_4', label: 'Monitoring 4' },
  { value: 'monitoring_5', label: 'Monitoring 5' },
  { value: 'endline', label: 'Endline' },
];

const STATUS_LABEL: Record<Period['status'], string> = {
  upcoming: 'Akan datang',
  active: 'Aktif',
  locked: 'Terkunci',
};

const periodApi = {
  list: () => api.get<Period[]>('/periods'),
  create: (body: PeriodInput) => api.post('/periods', body),
  update: (id: string, body: PeriodInput) => api.patch(`/periods/${id}`, body),
  remove: (id: string) => api.del(`/periods/${id}`),
  activate: (id: string) => api.post(`/periods/${id}/activate`),
  lock: (id: string) => api.post(`/periods/${id}/lock`),
  unlock: (id: string) => api.post(`/periods/${id}/unlock`),
};

/** L-16 — Kelola periode evaluasi: buat, aktifkan, kunci. */
export function PeriodsPage() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Period | null | undefined>(undefined);
  const [deleting, setDeleting] = useState<Period | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  const { data, isLoading, error: loadError, refetch } = useQuery({
    queryKey: ['periods'],
    queryFn: () => periodApi.list(),
  });

  // PeriodSwitcher di header ikut memakai daftar ini, jadi keduanya disegarkan.
  const refresh = () => queryClient.invalidateQueries();
  const fail = (err: unknown) =>
    setError(err instanceof ApiError ? err.message : 'Gagal menyimpan perubahan.');

  const save = useMutation({
    mutationFn: (input: PeriodInput) =>
      editing ? periodApi.update(editing.id, input) : periodApi.create(input),
    onSuccess: () => {
      refresh();
      setEditing(undefined);
      setError(null);
      setBanner('Periode tersimpan.');
    },
    onError: fail,
  });

  const act = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'activate' | 'lock' | 'unlock' }) =>
      periodApi[action](id),
    onSuccess: () => {
      refresh();
      setBanner('Status periode diperbarui.');
    },
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: () => periodApi.remove(deleting!.id),
    onSuccess: () => {
      refresh();
      setDeleting(null);
      setBanner('Periode dihapus.');
    },
    onError: (err) => {
      setDeleting(null);
      fail(err);
    },
  });

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Periode Evaluasi"
        description="Semester penilaian. Satu periode aktif menentukan data mana yang sedang dikerjakan."
        action={<Button onClick={() => setEditing(null)}>+ Periode Baru</Button>}
      />

      {banner && <Banner tone="success">{banner}</Banner>}
      {error && editing === undefined && <Banner tone="danger">{error}</Banner>}

      {loadError ? (
        <ErrorState message={(loadError as Error).message} onRetry={() => refetch()} />
      ) : isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <Card padded={false} className="min-w-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[780px] border-collapse text-left">
              <thead>
                <tr className="border-b border-border">
                  <Th className="w-16">Sem.</Th>
                  <Th>Nama & Keterangan</Th>
                  <Th className="w-24">Tahun</Th>
                  <Th className="w-40">Rentang</Th>
                  <Th className="w-28">Status</Th>
                  <Th className="w-40">Aksi</Th>
                </tr>
              </thead>
              <tbody>
                {(data ?? []).map((p) => (
                  <tr key={p.id} className="border-b border-border align-top last:border-0">
                    <Td>
                      <span className="tabular font-semibold text-ink">{p.semester}</span>
                    </Td>
                    <Td>
                      <div>
                        <span className="font-medium text-ink">{p.name}</span>
                        {p.description && (
                          <p className="mt-0.5 text-[13px] text-ink-secondary">{p.description}</p>
                        )}
                      </div>
                    </Td>
                    <Td>
                      <span className="tabular text-ink">{p.programYear}</span>
                    </Td>
                    <Td>
                      <span className="text-[13px] text-ink-secondary">
                        {p.startDate ?? '—'} → {p.endDate ?? '—'}
                      </span>
                    </Td>
                    <Td>
                      <span
                        className={cn(
                          'rounded-full px-2.5 py-0.5 text-[12px] font-medium',
                          p.status === 'active'
                            ? 'bg-success-soft text-success'
                            : p.status === 'locked'
                              ? 'bg-app text-ink-secondary'
                              : 'bg-primary-soft text-primary',
                        )}
                      >
                        {STATUS_LABEL[p.status]}
                      </span>
                    </Td>
                    <Td>
                      <div className="flex flex-col gap-1">
                        {p.status !== 'active' && p.status !== 'locked' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => act.mutate({ id: p.id, action: 'activate' })}
                            disabled={act.isPending}
                          >
                            Aktifkan
                          </Button>
                        )}
                        {p.status !== 'locked' ? (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => setEditing(p)}>
                              Ubah
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => act.mutate({ id: p.id, action: 'lock' })}
                              disabled={act.isPending}
                            >
                              Kunci
                            </Button>
                          </>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => act.mutate({ id: p.id, action: 'unlock' })}
                            disabled={act.isPending}
                          >
                            Buka kunci
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => setDeleting(p)}>
                          Hapus
                        </Button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card>
        <p className="text-[13px] text-ink-secondary">
          <strong>Tahun program dihitung otomatis</strong> dari semester (1–2 → Tahun 1, 3–4 → Tahun
          2, 5–6 → Tahun 3), jadi tidak bisa diisi tangan. <strong>Mengunci</strong> periode
          menandai bahwa pengisiannya sudah selesai; mengaktifkan periode lain tidak akan membuka
          kunci itu. Periode yang sudah dipakai TBM sebagai periode bergabung tidak bisa dihapus —
          dari situlah tahun program dan ambang lulusnya dihitung.
        </p>
      </Card>

      {editing !== undefined && (
        <PeriodEditor
          period={editing}
          saving={save.isPending}
          error={error}
          onCancel={() => {
            setEditing(undefined);
            setError(null);
          }}
          onSave={(input) => save.mutate(input)}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        title="Hapus periode?"
        confirmLabel="Ya, hapus"
        loading={remove.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => remove.mutate()}
      >
        <p className="mb-3 rounded-[var(--radius-input)] bg-app px-3.5 py-2.5 font-medium text-ink">
          {deleting?.name}
        </p>
        <p>
          Seluruh data konfigurasi periode ini akan dihapus secara permanen.
        </p>
      </ConfirmDialog>
    </div>
  );
}

function PeriodEditor({
  period,
  saving,
  error,
  onCancel,
  onSave,
}: {
  period: Period | null;
  saving: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (input: PeriodInput) => void;
}) {
  const [semester, setSemester] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [phase, setPhase] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setSemester(period ? String(period.semester) : '');
    setName(period?.name ?? '');
    setDescription(period?.description ?? '');
    setPhase(period?.phase ?? '');
    setStartDate(period?.startDate ?? '');
    setEndDate(period?.endDate ?? '');
    setFieldErrors({});
  }, [period]);

  const semesterNum = Number(semester);
  const programYear =
    semester.trim() !== '' && !isNaN(semesterNum) && semesterNum >= 0
      ? semesterNum === 0
        ? 0
        : Math.ceil(semesterNum / 2)
      : null;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!semester.trim() || isNaN(semesterNum) || semesterNum < 0 || semesterNum > 6) {
      errors.semester = 'Semester antara 0 dan 6';
    }
    if (!name.trim()) errors.name = 'Nama periode wajib diisi';
    if (startDate && endDate && startDate > endDate) {
      errors.endDate = 'Tanggal akhir mendahului tanggal mulai';
    }

    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      return;
    }

    onSave({
      semester: semesterNum,
      name: name.trim(),
      description: description.trim() || null,
      phase: phase || null,
      startDate: startDate || null,
      endDate: endDate || null,
    });
  }

  return (
    <form
      onSubmit={submit}
      noValidate
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-0 sm:items-center sm:p-4"
    >
      <div className="w-full max-w-lg rounded-t-[var(--radius-card)] bg-surface p-5 shadow-[var(--radius-card)] sm:rounded-[var(--radius-card)] sm:shadow-[var(--shadow-hover)]">
        <h2 className="text-[19px] font-semibold text-ink">
          {period ? `Ubah ${period.name}` : 'Periode Baru'}
        </h2>

        {error && (
          <div className="mt-3">
            <Banner tone="danger">{error}</Banner>
          </div>
        )}

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Input
            label="Semester"
            type="number"
            inputMode="numeric"
            value={semester}
            onChange={(e) => setSemester(e.target.value)}
            error={fieldErrors.semester}
            hint={programYear !== null ? `Tahun Program ${programYear}` : '0–6'}
            required
          />
          <Select label="Fase" value={phase} onChange={(e) => setPhase(e.target.value)}>
            <option value="">— tidak ditentukan —</option>
            {PHASES.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </Select>
        </div>

        <div className="mt-4">
          <Input
            label="Nama periode"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={fieldErrors.name}
            hint="Mis. Semester 3 — Monitoring 2"
            required
          />
        </div>

        <div className="mt-4">
          <label className="mb-1 block text-[13px] font-medium text-ink">
            Keterangan / Catatan
          </label>
          <textarea
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Penjelasan atau catatan fokus pelaksanaan periode ini…"
            className="w-full rounded-[var(--radius-input)] border border-border bg-surface px-3.5 py-2.5 text-[14px] text-ink placeholder:text-ink-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Input
            label="Mulai"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <Input
            label="Selesai"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            error={fieldErrors.endDate}
          />
        </div>

        <div className="mt-5 flex flex-col-reverse gap-2.5 sm:flex-row sm:justify-end">
          <Button type="button" variant="secondary" onClick={onCancel} disabled={saving}>
            Batal
          </Button>
          <Button type="submit" loading={saving}>
            Simpan
          </Button>
        </div>
      </div>
    </form>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      scope="col"
      className={cn('px-4 py-3 text-[13px] font-semibold text-ink-secondary', className)}
    >
      {children}
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return <td className="px-4 py-3.5 align-top">{children}</td>;
}
